'use client';

import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import type { LineItemFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { LineItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LineItemFragmentFragmentDoc';
import { MiniDVLayer } from '@packages/modules/src/MiniDVLayer/MiniDVLayer.client';
import { buildGlycerinCartProduct } from '@packages/modules/src/helpers/buildGlycerinObject/buildGlycerinObject';
import { useState } from 'react';
import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import { MoreMenu } from '../MoreMenu/MoreMenu';
import { ActionBar } from '../ActionBar/ActionBar';
import { BasketAplus } from '../Aplus/BasketAplus.client';
import { EditAction } from '../Actions/EditAction';
import { AddToWishlistAction } from '../Actions/AddToWishlistAction';
import { DeleteAction } from '../Actions/DeleteAction';
import { basketLineItemToDisplayProps } from './LineItemTypes';
import { LineItemContent } from './LineItemContent.client';

/* GraphQL */ `
  fragment LineItemFragment on BasketLineItem {
    id
    product {
      akl
      sku
      orderNumber
      promotion
      size
      imageUrl
      name
      brand
      powerEfficiencyFlags {
        text
        level
        link
        dataSheet
        color
        hasOldLabel
      }
      tireEfficiencyFlags {
        type
        level
        link
        dataSheet
      }
      variation
      sustained
      marketingFlag {
        text
        type
      }
      powerSupply {
        chargerType
        maxPower
        minPower
        powerSupplyIncluded
      }
      additionalFlags {
        src
        text
        type
      }
    }
    price {
      amount
      currency
      isStartPrice
      saving {
        type
        value
      }
      unit
      uvp
      value
      valueOld
      valueUnit
      discount {
        discountAmount
        discountPercentage
        amountBeforeDiscount
      }
    }
    singlePrice {
      amount
      currency
    }
    availability {
      message
      state
      limitedStock
    }
    configuration {
      length
      width
      engraving
      serialNumber
    }
    aplus {
      ...AplusFragment
    }
    quantity
    isSoldOut
  }
`;

type LineItemActionsProps = {
  sku: LineItemFragmentFragment['product']['sku'];
  lineItemId: LineItemFragmentFragment['id'];
  glycerinProduct: ReturnType<typeof buildGlycerinCartProduct>;
  quantity: LineItemFragmentFragment['quantity'];
  akl: LineItemFragmentFragment['product']['akl'];
};

const LineItemActions = ({
  lineItemId,
  sku,
  glycerinProduct,
  quantity,
  akl,
}: LineItemActionsProps) => {
  const isDesktop = useMediaType();
  const [isMiniDVOpen, setIsMiniDVOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const children = [
    <EditAction
      mode="basket"
      key="edit"
      onClick={() => {
        setIsMiniDVOpen(true);
        setIsMoreMenuOpen(false);
      }}
    />,
    <AddToWishlistAction
      key="wishlist"
      sku={sku}
      lineItemId={lineItemId}
      glycerinProduct={glycerinProduct}
      onClick={() => {
        setIsMoreMenuOpen(false);
      }}
    />,
    <DeleteAction
      mode="basket"
      key="delete"
      lineItemId={lineItemId}
      glycerinProduct={glycerinProduct}
      onClick={() => {
        setIsMoreMenuOpen(false);
      }}
    />,
  ];

  return (
    <>
      {isDesktop ? (
        <ActionBar>{children}</ActionBar>
      ) : (
        <MoreMenu isOpen={isMoreMenuOpen} onOpenChange={setIsMoreMenuOpen}>
          {children}
        </MoreMenu>
      )}
      {lineItemId && isMiniDVOpen && (
        <MiniDVLayer
          sku={sku}
          akl={akl}
          lineItemId={lineItemId}
          quantity={quantity}
          open={isMiniDVOpen}
          onClose={() => setIsMiniDVOpen(false)}
          mode="ATB"
        />
      )}
    </>
  );
};

type BasketLineItemProps = {
  lineItemData: FragmentType<typeof LineItemFragmentFragmentDoc>;
};

export const BasketLineItem = ({ lineItemData }: BasketLineItemProps) => {
  const fragmentData = getFragmentData(LineItemFragmentFragmentDoc, lineItemData);
  const glycerinProduct = buildGlycerinCartProduct(fragmentData);
  const { product, id, quantity, aplus } = fragmentData;
  const displayProps = basketLineItemToDisplayProps(fragmentData);

  return (
    <LineItemContent
      {...displayProps}
      glycerinProduct={glycerinProduct}
      actionsSlot={
        <LineItemActions
          sku={product.sku}
          lineItemId={id}
          glycerinProduct={glycerinProduct}
          akl={product.akl}
          quantity={quantity}
        />
      }
      aplusSlot={
        aplus ? <BasketAplus aplusData={aplus} lineItemId={id!} quantity={quantity} /> : undefined
      }
    />
  );
};
